/**
 * Database Path Utilities
 *
 * Centralized path definitions for the new nested Firestore structure:
 * clients/{clientId}/{collection}/{docId}/{subcollection}
 */

const CLIENT_ID = 'athleto';

// Root paths
export const DB_PATHS = {
  // Client root
  CLIENT: `clients/${CLIENT_ID}`,
  GLOBAL: 'global',

  // Top-level collections under client
  USERS: `clients/${CLIENT_ID}/directory/users/main`, // New nested structure
  ORGANIZATIONS: `clients/${CLIENT_ID}/organizations`,
  ORGANIZATION_MEMBERSHIPS: `clients/${CLIENT_ID}/organization_memberships`,
  ORGANIZATION_INVITATIONS: `clients/${CLIENT_ID}/organization_invitations`,
  CHALLENGES: `clients/${CLIENT_ID}/challenges`,
  CONVERSATIONS: `clients/${CLIENT_ID}/conversations`,
  FOLLOWS: `clients/${CLIENT_ID}/follows`,
  LIKES: `clients/${CLIENT_ID}/likes`,
  COMMENTS: `clients/${CLIENT_ID}/comments`,
  RECRUITER_VERIFICATION_REQUESTS: `clients/${CLIENT_ID}/recruiter_verification_requests`,
  USER_COINS: `clients/${CLIENT_ID}/user_coins`,
  TEAMS: `clients/${CLIENT_ID}/teams`, // Top-level teams for backwards compatibility
};

// Helper functions to build document and subcollection paths

// Users
export const userPath = (userId: string) => `${DB_PATHS.USERS}/${userId}`;
export const userSessionsPath = (userId: string) => `${userPath(userId)}/sessions`;
export const userSessionPath = (userId: string, sessionId: string) => `${userSessionsPath(userId)}/${sessionId}`;
export const userNotificationsPath = (userId: string) => `${userPath(userId)}/notifications`;
export const userNotificationPath = (userId: string, notificationId: string) => `${userNotificationsPath(userId)}/${notificationId}`;

// Organizations
export const organizationPath = (orgId: string) => `${DB_PATHS.ORGANIZATIONS}/${orgId}`;
export const organizationTeamsPath = (orgId: string) => `${organizationPath(orgId)}/teams`;
export const organizationTeamPath = (orgId: string, teamId: string) => `${organizationTeamsPath(orgId)}/${teamId}`;
export const teamPostsPath = (orgId: string, teamId: string) => `${organizationTeamPath(orgId, teamId)}/posts`;
export const teamPostPath = (orgId: string, teamId: string, postId: string) => `${teamPostsPath(orgId, teamId)}/${postId}`;

// Challenges
export const challengePath = (challengeId: string) => `${DB_PATHS.CHALLENGES}/${challengeId}`;
export const challengeCompletionsPath = (challengeId: string) => `${challengePath(challengeId)}/completions`;
export const challengeCompletionPath = (challengeId: string, completionId: string) => `${challengeCompletionsPath(challengeId)}/${completionId}`;

// Conversations
export const conversationPath = (conversationId: string) => `${DB_PATHS.CONVERSATIONS}/${conversationId}`;
export const conversationMessagesPath = (conversationId: string) => `${conversationPath(conversationId)}/messages`;
export const conversationMessagePath = (conversationId: string, messageId: string) => `${conversationMessagesPath(conversationId)}/${messageId}`;
export const conversationTypingIndicatorsPath = (conversationId: string) => `${conversationPath(conversationId)}/typing_indicators`;
export const conversationTypingIndicatorPath = (conversationId: string, userId: string) => `${conversationTypingIndicatorsPath(conversationId)}/${userId}`;

// Teams (top-level)
export const teamPath = (teamId: string) => `${DB_PATHS.TEAMS}/${teamId}`;

// Team Subcollections
export const playbookCategoriesPath = (teamId: string) => `${teamPath(teamId)}/playbook_categories`;
export const playbookCategoryPath = (teamId: string, categoryId: string) => `${playbookCategoriesPath(teamId)}/${categoryId}`;
export const playbookPlaysPath = (teamId: string) => `${teamPath(teamId)}/playbook_plays`;
export const playbookPlayPath = (teamId: string, playId: string) => `${playbookPlaysPath(teamId)}/${playId}`;
export const documentRequirementsPath = (teamId: string) => `${teamPath(teamId)}/document_requirements`;
export const documentRequirementPath = (teamId: string, requirementId: string) => `${documentRequirementsPath(teamId)}/${requirementId}`;
export const documentUploadsPath = (teamId: string) => `${teamPath(teamId)}/document_uploads`;
export const documentUploadPath = (teamId: string, uploadId: string) => `${documentUploadsPath(teamId)}/${uploadId}`;
export const scheduledEventsPath = (teamId: string) => `${teamPath(teamId)}/scheduled_events`;
export const scheduledEventPath = (teamId: string, eventId: string) => `${scheduledEventsPath(teamId)}/${eventId}`;
export const teamVideosPath = (teamId: string) => `${teamPath(teamId)}/team_videos`;
export const teamVideoPath = (teamId: string, videoId: string) => `${teamVideosPath(teamId)}/${videoId}`;
export const teamVideoCategoriesPath = (teamId: string) => `${teamPath(teamId)}/team_video_categories`;
export const teamVideoCategoryPath = (teamId: string, categoryId: string) => `${teamVideoCategoriesPath(teamId)}/${categoryId}`;

// Mail (top-level under client)
export const mailPath = () => `clients/${CLIENT_ID}/mail`;
export const mailDocPath = (mailId: string) => `${mailPath()}/${mailId}`;

// Follows
export const followPath = (followId: string) => `${DB_PATHS.FOLLOWS}/${followId}`;

// Likes
export const likePath = (likeId: string) => `${DB_PATHS.LIKES}/${likeId}`;

// Comments
export const commentPath = (commentId: string) => `${DB_PATHS.COMMENTS}/${commentId}`;

// Recruiter Verification
export const recruiterVerificationRequestPath = (requestId: string) => `${DB_PATHS.RECRUITER_VERIFICATION_REQUESTS}/${requestId}`;

// User Coins
export const userCoinsPath = (userId: string) => `${DB_PATHS.USER_COINS}/${userId}`;

// Organization Memberships
export const organizationMembershipPath = (membershipId: string) => `${DB_PATHS.ORGANIZATION_MEMBERSHIPS}/${membershipId}`;

// Organization Invitations
export const organizationInvitationPath = (invitationId: string) => `${DB_PATHS.ORGANIZATION_INVITATIONS}/${invitationId}`;

// Collection group paths (for querying across all subcollections)
export const COLLECTION_GROUPS = {
  SESSIONS: 'sessions',
  NOTIFICATIONS: 'notifications',
  TEAMS: 'teams',
  POSTS: 'posts',
  COMPLETIONS: 'completions',
  MESSAGES: 'messages',
  TYPING_INDICATORS: 'typing_indicators',
};

// Storage paths
export const STORAGE_PATHS = {
  SESSIONS: (userId: string, sessionId: string) => `sessions/${userId}/${sessionId}`,
  PROFILES: (userId: string) => `profiles/${userId}`,
  MESSAGES: (conversationId: string) => `messages/${conversationId}`,
  ORGANIZATIONS: (orgId: string) => `organizations/${orgId}`,
  TEAMS: (teamId: string) => `teams/${teamId}`,
};
